import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { createSelectorHook, useDispatch } from 'react-redux';
import { Loader, Modal as ModalMUI } from '~/components';
import Accept from '~/compositions/TermsAndPolicy/Accept';
import ThankYou from '~/compositions/TermsAndPolicy/ThankYou';
import { updateTermsAndPolicy } from '~/store/modules/termsAndPolicy/actions';
import AreYouSure from '~/compositions/TermsAndPolicy/AreYouSure';
import messages from '~/helpers/api/messages';
import { ToastCtx } from '~/providers/ToastProvider';

const steps = {
  ACCEPT: 'ACCEPT',
  THANKS: 'THANKS',
  YOU_SURE: 'YOU_SURE',
  CLOSE_MODAL: 'CLOSE_MODAL',
};

const Modal = styled(ModalMUI)`
  .MuiDialog-paper {
    max-width: ${({ step }) =>
      step === steps?.ACCEPT || step === steps?.YOU_SURE ? '735px' : '643px'};
  }
`;

type RootState = {
  user: {
    id: number;
    name: string;
    cpf: string;
    email: string;
    firstAccess: boolean;
    firstDashboardAccess: boolean;
    cookieConsent: boolean;
    termoUsoUsuario: {
      linkTermoUso: string;
      linkPoliticaPrivacidade: string;
      dataLimiteAceite: string;
      intervaloNotificaUsuario: number;
      flagStatusAceite: boolean;
      dataAtualizacaoTermo: string;
    };
  };
  loading: boolean;
};

export default function TermsAndPolicy() {
  const dispatch = useDispatch();
  const toast = useContext(ToastCtx);
  const useSelector = createSelectorHook<RootState>();
  const user = useSelector(state => state?.user);
  const loading = useSelector(state => state?.loading);
  const flag = user?.termoUsoUsuario?.flagStatusAceite;
  const [step, setStep] = useState<string>(steps?.ACCEPT);
  const [open, setOpen] = useState(false);
  const [shouldAccept, setShouldAccept] = useState(false);

  function dateUTC(date: Date): number {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function formatDate(date: string): Date {
    return new Date(date?.replaceAll('-', '/'));
  }

  function diffInDays(a: number, b: number) {
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    return Math.floor((a - b) / MS_PER_DAY);
  }

  function notifyUser() {
    const intervalToNotify = user?.termoUsoUsuario?.intervaloNotificaUsuario;
    const dateLastUpdate = dateUTC(
      formatDate(user?.termoUsoUsuario?.dataAtualizacaoTermo),
    );
    const limitDate = dateUTC(
      formatDate(user?.termoUsoUsuario?.dataLimiteAceite),
    );
    const nowUTC = dateUTC(new Date());
    const diff = diffInDays(nowUTC, dateLastUpdate);
    const timeToNotify = !flag && diff > intervalToNotify;

    const diffDeadline = diffInDays(limitDate, nowUTC);
    const isDeadline = !flag && diffDeadline <= 0;

    if (isDeadline) {
      setShouldAccept(true);
      setStep(steps?.ACCEPT);
      setOpen(true);
    } else if (timeToNotify) {
      setStep(steps?.ACCEPT);
      setOpen(true);
    }
  }

  useEffect(() => {
    if (!flag) {
      notifyUser();
    }
  }, []);

  function onFailureReminder(e: Object): void {
    toast.dispatch(messages(e, 'terms_and_policy'));
  }

  function handleAccept(): void {
    dispatch(
      updateTermsAndPolicy(
        true,
        user,
        () => setStep(steps.THANKS),
        onFailureReminder,
      ),
    );
  }

  function handleReminder(nextStep?: string): void {
    function onSuccessReminder() {
      setOpen(false);
      setTimeout(() => {
        setStep(steps?.ACCEPT);
      }, 300);
    }

    if (nextStep === steps.YOU_SURE) {
      setStep(nextStep);
    }

    if (nextStep === steps.CLOSE_MODAL) {
      dispatch(
        updateTermsAndPolicy(
          false,
          user,
          () => onSuccessReminder(),
          onFailureReminder,
        ),
      );
    }
  }

  const content = {
    [steps.ACCEPT]: (
      <Accept
        shouldAccept={shouldAccept}
        onAccept={handleAccept}
        onReminder={() => handleReminder(steps.YOU_SURE)}
      />
    ),
    [steps.THANKS]: <ThankYou />,
    [steps.YOU_SURE]: (
      <AreYouSure
        onBack={() => setStep(steps?.ACCEPT)}
        onReminder={() => handleReminder(steps.CLOSE_MODAL)}
      />
    ),
  };

  function handleClose() {
    setOpen(false);
  }

  return (
    // @ts-ignore
    <Modal
      step={step}
      open={open}
      fullWidth
      {...(step === steps.THANKS && { handleClose })}
    >
      {/* @ts-ignore */}
      <Loader isLoading={loading}>{content[step]}</Loader>
    </Modal>
  );
}
